import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import Drawer from "@via/components/drawer";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import { RUN_ENVIRONMENTS_TABLE } from "@via/utils/constants.tsx";
import { removeFalsyValues } from "@via/utils/helpers.tsx";
import type { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { type ProfileClusterForClient } from "common";
import { JSX, useState } from "react";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { useDeleteOrganizationRunEnvironment } from "@via/pages/organization/run-environments/organization-run-environments.api.tsx";
import OrganizationRunEnvironmentForm from "@via/pages/organization/run-environments/run-environments-form.tsx";
import OrganizationPremiumRunEnvironmentForm from "@via/pages/organization/run-environments/premium-run-environments-form.tsx";

const columnHelper = createColumnHelper<ProfileClusterForClient>();

export default function OrganizationRunEnvironmentsTable({
  organizationId,
  runEnvironments,
}: {
  organizationId: string;
  runEnvironments: ProfileClusterForClient[];
}): JSX.Element {
  const [isFormDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editableRunEnvironment, setEditableRunEnvironment] =
    useState<ProfileClusterForClient>();
  const [runEnvironmentToBeDeleted, setRunEnvironmentToBeDeleted] =
    useState<ProfileClusterForClient | null>(null);
  const {
    mutateAsync: deleteRunEnvironment,
    isPending: isDeleteRunEnvironmentLoading,
  } = useDeleteOrganizationRunEnvironment();

  function handleEditAction(runEnvironment: ProfileClusterForClient) {
    setEditableRunEnvironment(
      removeFalsyValues(runEnvironment) as ProfileClusterForClient
    );
    setFormDrawerOpen(true);
  }

  function handleRemoveAction(runEnvironment: ProfileClusterForClient) {
    setRunEnvironmentToBeDeleted(runEnvironment);
  }

  function handleAction(
    option: TableContextMenuInterface,
    runEnvironment: ProfileClusterForClient
  ) {
    switch (option.name) {
      case RUN_ENVIRONMENTS_TABLE[0].name:
        handleEditAction(runEnvironment);
        break;
      case RUN_ENVIRONMENTS_TABLE[1].name:
        handleRemoveAction(runEnvironment);
        break;
      default:
        break;
    }
  }

  const columns = [
    columnHelper.accessor("id", {
      header: () => <span>Id</span>,
      cell: info => {
        const value = info.getValue();
        if (value === null || value === undefined) return "-";
        return (
          <span className="font-mono text-xs text-gray-700">
            {value.toString()}
          </span>
        );
      },
    }),
    columnHelper.accessor("name", {
      header: () => <span>{"Profile Name"}</span>,
      cell: info => info.getValue(),
    }),
    columnHelper.accessor("containerType", {
      header: () => <span>Container Type</span>,
      cell: info => {
        const { isSystemGenerated } = info.row.original;
        if (isSystemGenerated) return "-";
        return info.getValue();
      },
    }),
    columnHelper.accessor("nextPath", {
      header: () => <span>Details</span>,
      cell: info => {
        const { row } = info;
        const { nextPath, executor, username, hostname, isSystemGenerated } =
          row.original;
        if (isSystemGenerated) return "-";
        return (
          <div>
            <div>Workflow Directory Path: {nextPath}</div>
            <div>Executor: {executor}</div>
            <div>
              Connection: {username}@{hostname}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "options",
      header: () => "Options",
      cell: info => {
        const { isSystemGenerated } = info.row.original;
        const filteredOptions = RUN_ENVIRONMENTS_TABLE.filter(
          option => option.name !== "Remove"
        );
        if (isSystemGenerated) {
          return (
            <TableContextMenu
              options={filteredOptions}
              onSelect={option => {
                handleAction(option, info.row.original);
              }}
            />
          );
        }

        return (
          <TableContextMenu
            options={RUN_ENVIRONMENTS_TABLE}
            onSelect={option => {
              handleAction(option, info.row.original);
            }}
          />
        );
      },
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ProfileClusterForClient>>;

  return (
    <div>
      <Table
        columns={columns}
        data={runEnvironments || []}
        total={runEnvironments?.length}
      />

      <Drawer
        id="editable-run-environment"
        open={isFormDrawerOpen}
        setOpen={setFormDrawerOpen}>
        {editableRunEnvironment?.isSystemGenerated ? (
          <OrganizationPremiumRunEnvironmentForm
            setOpen={setFormDrawerOpen}
            organizationId={organizationId}
            editableRunEnvironment={editableRunEnvironment}
          />
        ) : (
          <OrganizationRunEnvironmentForm
            setOpen={setFormDrawerOpen}
            organizationId={organizationId}
            editableRunEnvironment={editableRunEnvironment}
          />
        )}
      </Drawer>

      <DeleteModal
        item={runEnvironmentToBeDeleted}
        setItem={setRunEnvironmentToBeDeleted}
        deleteFunction={() =>
          deleteRunEnvironment({
            id: organizationId,
            runEnvironmentId: runEnvironmentToBeDeleted!.id,
          })
        }
        isLoading={isDeleteRunEnvironmentLoading}
        title="Delete Run Environment"
      />
    </div>
  );
}
